/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useEffect, useRef } from 'react';

import { bindAll } from 'bind-event-listener';

import { cssMap, jsx } from '@atlaskit/css';

const styles = cssMap({
	// The boundary only marks which elements belong to the content, so it must not affect layout.
	root: { display: 'contents' },
});

const nativeItemSelector = 'a[href], button';
const itemSelector = `${nativeItemSelector}, [role="menuitem"]`;

/**
 * Items that keep the popup open: they are unavailable, they open or expand something the user
 * still needs the popup for, or they toggle a selection the user may change several times.
 */
const keepsPopupOpenSelector = [
	'[aria-disabled="true"]',
	'[aria-expanded]',
	'[aria-haspopup]:not([aria-haspopup="false"])',
	'[role="menuitemcheckbox"]',
	'[role="menuitemradio"]',
].join(', ');

// The surfaces ADS layers render: the legacy popup wrapper, top-layer popovers, and dialogs.
const layerSelector = '[data-ds--level], [popover], dialog, [role="dialog"]';

type CloseOnItemClickProps = {
	children: ReactNode;
	onItemClick: (event: MouseEvent | KeyboardEvent) => void;
};

/**
 * Returns the item in the popup's own layer that the event activated, unless the item keeps the
 * popup open.
 */
function getClosingItem(boundary: HTMLElement, event: MouseEvent | KeyboardEvent): Element | null {
	// Modifier clicks open links in a new tab or window, so the user is still here.
	if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
		return null;
	}

	if (!(event.target instanceof Element)) {
		return null;
	}

	const item = event.target.closest(itemSelector);

	// Containment is checked in the DOM, so a layer the content opens in a portal — a modal, say —
	// does not count as the content. A layer it opens inline is inside the boundary, so the item
	// must also share the boundary's nearest layer.
	if (
		!item ||
		!boundary.contains(item) ||
		item.closest(layerSelector) !== boundary.closest(layerSelector) ||
		item.matches(keepsPopupOpenSelector)
	) {
		return null;
	}

	return item;
}

/**
 * Calls `onItemClick` after an item in `children` handles its activation, so the popup closes
 * without every item having to close it.
 */
export function CloseOnItemClick({ children, onItemClick }: CloseOnItemClickProps): JSX.Element {
	const boundaryRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const boundary = boundaryRef.current;

		if (!boundary) {
			return;
		}

		// Listening on the document in the bubble phase runs after React has dispatched the event to
		// the item, which it does from its root container. An item that stops propagation keeps the
		// popup open.
		return bindAll(boundary.ownerDocument, [
			{
				type: 'click',
				listener: (event) => {
					if (getClosingItem(boundary, event)) {
						onItemClick(event);
					}
				},
			},
			{
				// Enter on a link or button dispatches a click, which the listener above handles. No
				// click follows when the item handles Enter itself and prevents the default, as router
				// links do, or when the item is not a native link or button.
				type: 'keydown',
				listener: (event) => {
					if (event.key !== 'Enter' || event.repeat) {
						return;
					}

					const item = getClosingItem(boundary, event);

					if (item && (event.defaultPrevented || !item.matches(nativeItemSelector))) {
						onItemClick(event);
					}
				},
			},
		]);
	}, [onItemClick]);

	return (
		<div ref={boundaryRef} css={styles.root}>
			{children}
		</div>
	);
}
